// controllers/loader-controller.ts
import { html, render } from "lit";
import { AsyncDirective } from "lit/async-directive.js";
import { directive } from "lit/directive.js";
import {
  BehaviorSubject,
  combineLatest,
  concat,
  debounceTime,
  firstValueFrom,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  tap
} from "rxjs";
import { distinctUntilChanged, filter } from "rxjs/operators";
import { acss } from "../directives/acss.js";
import { css } from "../directives/css.js";
import "../elements/components/x-flex.js";
import "../elements/components/x-loader.js";
import "../renderable.js";
import { loaderSymbol } from "../symbols.js";
import { chemistryRootElement } from "./../chemistry-root-element.js";
import "./chemistry-controller.js";
var getRootLoader = async () => (await chemistryRootElement)[loaderSymbol];
var globalLoaderDiv = document.createElement("div");
document.body.appendChild(globalLoaderDiv);
var showGlobalLoaderDiv = () => {
  globalLoaderDiv.style.display = "block";
  globalLoaderDiv.style.opacity = "1";
  globalLoaderDiv.style.pointerEvents = "initial";
  globalLoaderDiv.style.position = "fixed";
  globalLoaderDiv.style.inset = "0";
  globalLoaderDiv.style.transition = "opacity 0.2s ease";
  globalLoaderDiv.style.zIndex = "1000";
  globalLoaderDiv.style.background = "#fff";
  globalLoaderDiv.setAttribute("global-loader", "");
};
var hideGlobalLoaderDiv = () => {
  globalLoaderDiv.style.display = "block";
  globalLoaderDiv.style.opacity = "0";
  globalLoaderDiv.style.pointerEvents = "none";
};
var GuardDirective = class extends AsyncDirective {
  render(loading, template, loaderTemplate) {
    return this.makeTemplate(loading, template, loaderTemplate);
  }
  update(_part, [loading, template, loaderTemplate]) {
    return this.makeTemplate(loading, template, loaderTemplate);
  }
  makeTemplate(loading, template, loaderTemplate) {
    return html`
      <div ${acss({ position: "relative", ">*": { transition: "all .5s ease" }, height: "100%", width: "100%" })}>
        <div ${css({ opacity: loading ? "0" : "1", height: "100%", width: "100%" })}>${template}</div>
        <div
          ${css({
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      opacity: loading ? "1" : "0",
      pointerEvents: loading ? "initial" : "none"
    })}
        >
          ${loaderTemplate || html`
            <x-flex h100 w100 center crossaxis-center ${css({ background: "#fff" })}> <x-loader></x-loader> </x-flex>
          `}
        </div>
      </div>
    `;
  }
};
var loadingGuardDirective = directive(GuardDirective);
var LoaderController = class {
  constructor(host, { loaderTemplate, globalLoaderTemplate, useGlobalLoader, waitForProperties = [] } = {}) {
    this.taskList = new BehaviorSubject([]);
    this.waitForProperties = new BehaviorSubject([]);
    this.propertiesDone = new BehaviorSubject(false);
    this.hasLoaded = false;
    this.isDone$ = combineLatest([this.taskList, this.propertiesDone.pipe(distinctUntilChanged())]).pipe(
      tap(() => {
        try {
          this.loadingCallback();
        } catch {
        }
      }),
      switchMap(
        ([list]) => concat(
          of(false),
          Promise.all(list.flatMap((e) => e.tasks)).then(() => true)
        )
      ),
      debounceTime(100),
      tap((done) => {
        done = done && this.propertiesDone.getValue();
        this.hasLoaded = done;
        if (done)
          this.loadedCallback();
        this._host.requestUpdate();
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.guard = (template, loaderTemplate) => {
      return loadingGuardDirective(
        this.isLoading,
        template,
        loaderTemplate || this.options.loaderTemplate
      );
    };
    this._host = host;
    this._host[loaderSymbol] = this;
    this.options = {
      loaderTemplate,
      globalLoaderTemplate,
      useGlobalLoader,
      waitForProperties
    };
    if (globalLoaderTemplate)
      render(globalLoaderTemplate, globalLoaderDiv);
    this.loadingCallback();
    this.isDone$.pipe(takeUntil(this._host.disconnected)).subscribe();
    this.waitForProperties.next(waitForProperties);
    host.addController(this);
  }
  get isLoading() {
    return !this.hasLoaded;
  }
  get isGlobalLoader() {
    return getRootLoader().then((rl) => rl === this);
  }
  async waitFor(...newTasks) {
    if (!newTasks.length)
      return;
    if (this.options.useGlobalLoader && !await this.isGlobalLoader) {
      return (await getRootLoader())?.waitFor(...newTasks);
    } else {
      this.taskList.next([
        ...this.taskList.getValue(),
        {
          host: this._host,
          tasks: newTasks.map(
            (task) => (
              // convert all non-promises to promises
              task.subscribe ? firstValueFrom(task.pipe(filter(Boolean))).catch((e) => {
                if (e.name === "EmptyError")
                  return;
                else
                  throw e;
              }) : task
            )
          )
        }
      ]);
      if (await this.isGlobalLoader)
        globalLoaderDiv.waitingFor = this.taskList.value;
      return await firstValueFrom(this.isDone$.pipe(filter(Boolean), takeUntil(this._host.disconnected))).catch(
        () => {
        }
      );
    }
  }
  async loadingCallback() {
    if (await this.isGlobalLoader)
      showGlobalLoaderDiv();
  }
  async loadedCallback() {
    if (await this.isGlobalLoader)
      hideGlobalLoaderDiv();
  }
  static async waitFor(...newTasks) {
    return (await getRootLoader())?.waitFor(...newTasks);
  }
  // wait for host properties logic
  hostUpdated() {
    if (this.options.waitForProperties?.length) {
      if (this.propertiesDone.getValue())
        return;
      const props = this.options.waitForProperties;
      const host = this._host;
      const done = props.every((prop) => {
        const currentValue = host[prop]?.host ? host[prop]?.value : host[prop];
        return currentValue !== void 0 && currentValue !== null && currentValue !== false;
      });
      this.propertiesDone.next(done);
    } else
      this.propertiesDone.next(true);
  }
};
export {
  LoaderController,
  loadingGuardDirective
};
